import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, CheckCircle, AlertCircle, Edit, X, Plus, Trash2 } from 'lucide-react';
import workAbroadService from '../../services/workAbroadService';

const WorkHistorySection = ({ profile, onRefresh }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    totalYearsExperience: '',
    currentEmploymentStatus: '',
    jobs: [],
    skills: [],
    certifications: [],
    languages: []
  });

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [newSkill, setNewSkill] = useState('');
  const [newLanguage, setNewLanguage] = useState({ language: '', proficiency: '' });

  useEffect(() => {
    if (profile?.workHistory) {
      const history = profile.workHistory;
      const hasData = history.totalYearsExperience || (history.jobs && history.jobs.length > 0);
      
      setFormData({
        totalYearsExperience: history.totalYearsExperience || '',
        currentEmploymentStatus: history.currentEmploymentStatus || '',
        jobs: history.jobs && history.jobs.length > 0 ? history.jobs : [],
        skills: history.skills || [],
        certifications: history.certifications || [],
        languages: history.languages || []
      });
      
      setIsEditing(!hasData);
    } else {
      setIsEditing(true);
    }
  }, [profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleJobChange = (index, field, value) => {
    const updatedJobs = [...formData.jobs];
    updatedJobs[index] = {
      ...updatedJobs[index],
      [field]: field === 'isCurrentJob' ? value : value
    };
    setFormData(prev => ({
      ...prev,
      jobs: updatedJobs
    }));
  };

  const addJob = () => {
    setFormData(prev => ({
      ...prev,
      jobs: [
        ...prev.jobs,
        {
          jobTitle: '',
          employer: '',
          country: '',
          city: '',
          startDate: '',
          endDate: '',
          isCurrentJob: false,
          responsibilities: '',
          achievements: '',
          reasonForLeaving: ''
        }
      ]
    }));
  };

  const removeJob = (index) => {
    setFormData(prev => ({
      ...prev,
      jobs: prev.jobs.filter((_, i) => i !== index)
    }));
  };

  const addSkill = () => {
    if (newSkill.trim()) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const removeSkill = (index) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index)
    }));
  };

  const handleCertificationChange = (index, field, value) => {
    const updatedCertifications = [...formData.certifications];
    updatedCertifications[index] = {
      ...updatedCertifications[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      certifications: updatedCertifications
    }));
  };

  const addCertification = () => {
    setFormData(prev => ({
      ...prev,
      certifications: [
        ...prev.certifications,
        {
          name: '',
          issuingOrganization: '',
          issueDate: '',
          expiryDate: '',
          credentialId: ''
        }
      ]
    }));
  };

  const removeCertification = (index) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const addLanguage = () => {
    if (newLanguage.language.trim() && newLanguage.proficiency) {
      setFormData(prev => ({
        ...prev,
        languages: [...prev.languages, { ...newLanguage }]
      }));
      setNewLanguage({ language: '', proficiency: '' });
    }
  };

  const removeLanguage = (index) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      await workAbroadService.updateWorkHistory(formData);
      setSuccessMessage('Work history saved successfully!');
      setIsEditing(false);
      if (onRefresh) onRefresh();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Failed to save work history');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (profile?.workHistory) {
      const history = profile.workHistory;
      setFormData({
        totalYearsExperience: history.totalYearsExperience || '',
        currentEmploymentStatus: history.currentEmploymentStatus || '',
        jobs: history.jobs || [],
        skills: history.skills || [],
        certifications: history.certifications || [],
        languages: history.languages || []
      });
    }
    setIsEditing(false);
    setErrorMessage('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const formatEmploymentStatus = (status) => {
    if (!status) return 'Not provided';
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatProficiency = (prof) => {
    if (!prof) return '';
    return prof.charAt(0).toUpperCase() + prof.slice(1);
  };

  return (
    <div className="p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header with Edit Button */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#202356] mb-2">Work History</h1>
            <p className="text-gray-600">
              {isEditing ? 'Provide your professional experience and qualifications' : 'Your professional background'}
            </p>
          </div>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-4 py-2 border border-[#202356] text-[#202356] rounded-lg font-medium hover:bg-[#202356] hover:text-white transition-colors"
            >
              <Edit size={18} />
              Edit
            </button>
          )}
        </div>

        {/* Success Message */}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3"
          >
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-green-700">{successMessage}</p>
          </motion.div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
          >
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-red-700">{errorMessage}</p>
          </motion.div>
        )}

        {/* View Mode */}
        {!isEditing ? (
          <div className="space-y-6">
            {/* Overview */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Years of Experience</p>
                  <p className="text-base font-medium text-gray-900">{formData.totalYearsExperience || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Current Employment Status</p>
                  <p className="text-base font-medium text-gray-900">{formatEmploymentStatus(formData.currentEmploymentStatus)}</p>
                </div>
              </div>
            </div>

            {/* Employment History */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Employment History</h2>
              {formData.jobs && formData.jobs.length > 0 ? (
                <div className="space-y-6">
                  {formData.jobs.map((job, index) => (
                    <div key={index} className="pb-6 border-b border-gray-200 last:border-b-0 last:pb-0">
                      <h3 className="font-semibold text-gray-900 mb-3">{job.jobTitle || 'Job ' + (index + 1)}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Employer</p>
                          <p className="text-base font-medium text-gray-900">{job.employer || 'Not provided'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Location</p>
                          <p className="text-base font-medium text-gray-900">{job.city && job.country ? `${job.city}, ${job.country}` : 'Not provided'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Duration</p>
                          <p className="text-base font-medium text-gray-900">
                            {formatDate(job.startDate)} - {job.isCurrentJob ? 'Present' : formatDate(job.endDate)}
                          </p>
                        </div>
                        {job.responsibilities && (
                          <div className="md:col-span-2">
                            <p className="text-sm text-gray-600 mb-1">Responsibilities</p>
                            <p className="text-base text-gray-900">{job.responsibilities}</p>
                          </div>
                        )}
                        {job.achievements && (
                          <div className="md:col-span-2">
                            <p className="text-sm text-gray-600 mb-1">Key Achievements</p>
                            <p className="text-base text-gray-900">{job.achievements}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No employment history added yet</p>
              )}
            </div>

            {/* Skills */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Skills</h2>
              {formData.skills && formData.skills.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {formData.skills.map((skill, index) => (
                    <span key={index} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                      {skill}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No skills added yet</p>
              )}
            </div>

            {/* Certifications */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Certifications</h2>
              {formData.certifications && formData.certifications.length > 0 ? (
                <div className="space-y-4">
                  {formData.certifications.map((cert, index) => (
                    <div key={index} className="pb-4 border-b border-gray-200 last:border-b-0 last:pb-0">
                      <h3 className="font-semibold text-gray-900 mb-2">{cert.name || 'Certification ' + (index + 1)}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <p className="text-sm text-gray-600">Issuing Organization</p>
                          <p className="text-base text-gray-900">{cert.issuingOrganization || 'Not provided'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Issue Date</p>
                          <p className="text-base text-gray-900">{formatDate(cert.issueDate)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No certifications added yet</p>
              )}
            </div>

            {/* Languages */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Languages</h2>
              {formData.languages && formData.languages.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.languages.map((lang, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium text-gray-900">{lang.language}</span>
                      <span className="text-sm text-gray-600">{formatProficiency(lang.proficiency)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No languages added yet</p>
              )}
            </div>
          </div>
        ) : (
          /* Edit Mode */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Overview */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Years of Experience *
                  </label>
                  <input
                    type="number"
                    name="totalYearsExperience"
                    value={formData.totalYearsExperience}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.5"
                    placeholder="e.g., 5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Employment Status *
                  </label>
                  <select
                    name="currentEmploymentStatus"
                    value={formData.currentEmploymentStatus}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                  >
                    <option value="">Select status</option>
                    <option value="employed">Employed</option>
                    <option value="unemployed">Unemployed</option>
                    <option value="self_employed">Self-Employed</option>
                    <option value="student">Student</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Employment History */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Employment History</h2>
                <button
                  type="button"
                  onClick={addJob}
                  className="flex items-center gap-2 px-4 py-2 bg-[#202356] text-white rounded-lg font-medium hover:bg-[#2542D4] transition-colors"
                >
                  <Plus size={18} />
                  Add Job
                </button>
              </div>

              {formData.jobs.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No jobs added yet. Click "Add Job" to begin.</p>
              ) : (
                <div className="space-y-6">
                  {formData.jobs.map((job, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg relative">
                      <button
                        type="button"
                        onClick={() => removeJob(index)}
                        className="absolute top-4 right-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>

                      <h3 className="font-semibold text-gray-900 mb-4">Job {index + 1}</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Job Title *
                          </label>
                          <input
                            type="text"
                            value={job.jobTitle}
                            onChange={(e) => handleJobChange(index, 'jobTitle', e.target.value)}
                            required
                            placeholder="e.g., Software Engineer"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Employer *
                          </label>
                          <input
                            type="text"
                            value={job.employer}
                            onChange={(e) => handleJobChange(index, 'employer', e.target.value)}
                            required
                            placeholder="e.g., Tech Company Ltd"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Country *
                          </label>
                          <input
                            type="text"
                            value={job.country}
                            onChange={(e) => handleJobChange(index, 'country', e.target.value)}
                            required
                            placeholder="e.g., United Kingdom"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            City *
                          </label>
                          <input
                            type="text"
                            value={job.city}
                            onChange={(e) => handleJobChange(index, 'city', e.target.value)}
                            required
                            placeholder="e.g., London"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Start Date *
                          </label>
                          <input
                            type="date"
                            value={job.startDate}
                            onChange={(e) => handleJobChange(index, 'startDate', e.target.value)}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            End Date {!job.isCurrentJob && '*'}
                          </label>
                          <input
                            type="date"
                            value={job.endDate}
                            onChange={(e) => handleJobChange(index, 'endDate', e.target.value)}
                            required={!job.isCurrentJob}
                            disabled={job.isCurrentJob}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356] disabled:bg-gray-100"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={job.isCurrentJob}
                              onChange={(e) => handleJobChange(index, 'isCurrentJob', e.target.checked)}
                              className="w-4 h-4 text-[#202356] border-gray-300 rounded focus:ring-[#202356]"
                            />
                            <span className="text-sm text-gray-700">I currently work here</span>
                          </label>
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Key Responsibilities
                          </label>
                          <textarea
                            value={job.responsibilities}
                            onChange={(e) => handleJobChange(index, 'responsibilities', e.target.value)}
                            rows="3"
                            placeholder="Describe your main responsibilities..."
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Key Achievements
                          </label>
                          <textarea
                            value={job.achievements}
                            onChange={(e) => handleJobChange(index, 'achievements', e.target.value)}
                            rows="3"
                            placeholder="Highlight your major achievements..."
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        {!job.isCurrentJob && (
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Reason for Leaving
                            </label>
                            <input
                              type="text"
                              value={job.reasonForLeaving}
                              onChange={(e) => handleJobChange(index, 'reasonForLeaving', e.target.value)}
                              placeholder="e.g., Career advancement"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Skills */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Skills</h2>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                  placeholder="Add a skill (e.g., Project Management)"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                />
                <button
                  type="button"
                  onClick={addSkill}
                  className="px-4 py-3 bg-[#202356] text-white rounded-lg font-medium hover:bg-[#2542D4] transition-colors"
                >
                  <Plus size={20} />
                </button>
              </div>
              {formData.skills.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.skills.map((skill, index) => (
                    <span key={index} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium flex items-center gap-2">
                      {skill}
                      <button
                        type="button"
                        onClick={() => removeSkill(index)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Certifications */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Certifications</h2>
                <button
                  type="button"
                  onClick={addCertification}
                  className="flex items-center gap-2 px-4 py-2 bg-[#202356] text-white rounded-lg font-medium hover:bg-[#2542D4] transition-colors"
                >
                  <Plus size={18} />
                  Add Certification
                </button>
              </div>

              {formData.certifications.length > 0 && (
                <div className="space-y-4">
                  {formData.certifications.map((cert, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg relative">
                      <button
                        type="button"
                        onClick={() => removeCertification(index)}
                        className="absolute top-4 right-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Certification Name
                          </label>
                          <input
                            type="text"
                            value={cert.name}
                            onChange={(e) => handleCertificationChange(index, 'name', e.target.value)}
                            placeholder="e.g., PMP Certification"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Issuing Organization
                          </label>
                          <input
                            type="text"
                            value={cert.issuingOrganization}
                            onChange={(e) => handleCertificationChange(index, 'issuingOrganization', e.target.value)}
                            placeholder="e.g., PMI"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Issue Date
                          </label>
                          <input
                            type="date"
                            value={cert.issueDate}
                            onChange={(e) => handleCertificationChange(index, 'issueDate', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Expiry Date (if applicable)
                          </label>
                          <input
                            type="date"
                            value={cert.expiryDate}
                            onChange={(e) => handleCertificationChange(index, 'expiryDate', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Credential ID
                          </label>
                          <input
                            type="text"
                            value={cert.credentialId}
                            onChange={(e) => handleCertificationChange(index, 'credentialId', e.target.value)}
                            placeholder="e.g., ABC123456"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Languages */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Languages</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
                <input
                  type="text"
                  value={newLanguage.language}
                  onChange={(e) => setNewLanguage(prev => ({ ...prev, language: e.target.value }))}
                  placeholder="Language (e.g., English)"
                  className="md:col-span-2 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                />
                <select
                  value={newLanguage.proficiency}
                  onChange={(e) => setNewLanguage(prev => ({ ...prev, proficiency: e.target.value }))}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                >
                  <option value="">Proficiency</option>
                  <option value="basic">Basic</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                  <option value="native">Native</option>
                </select>
              </div>
              <button
                type="button"
                onClick={addLanguage}
                className="w-full px-4 py-3 bg-[#202356] text-white rounded-lg font-medium hover:bg-[#2542D4] transition-colors flex items-center justify-center gap-2"
              >
                <Plus size={20} />
                Add Language
              </button>
              {formData.languages.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4">
                  {formData.languages.map((lang, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <span className="font-medium text-gray-900">{lang.language}</span>
                        <span className="text-sm text-gray-600 ml-2">({formatProficiency(lang.proficiency)})</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeLanguage(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X size={20} />
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-[#202356] text-white rounded-lg font-medium hover:bg-[#2542D4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default WorkHistorySection;
